import * as R from 'ramda';
import React, {useEffect, useState} from 'react';

/** tests a local storage implementation for problems
  * and returns a fake no op one that does not crash if problems occur */
export function getLocalStorage(candidateStorage) {
  let storage  = candidateStorage;
  if (storage) {
    const testKey = 'withLocalStorage-hoc-test-key';
    try {
      // Access to global `localStorage` property must be guarded as it
      // fails under iOS private session mode.
      storage.setItem( testKey, 'test');
      storage.removeItem(testKey);
    } catch (e) {
      storage = null;
    }
  }

  return storage || {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
  };
}

const storage = getLocalStorage(window.localStorage);

/** Returns a Higher order component that provides a state store backed by
 * the browser local storage. Note that currently only objects are supported
 * as state values
 *
 * Params:
 *
 * keyName :: String | Props -> String
 *
 * propName :: String
 * The name of the prop to pass the state in as
 * Contains either the default state or some state modified with the setter function
 * and stored in the local storage
 *
 * setterName :: String
 * The name of the prop that is passed the setter function.
 * The setter sets the current state and stores it in the local storage.
 *
 * clearerName :: String
 * The name of the prop that is passed the function to clear the stored data.
 * Clears the local storage for this component and resets the unstored state to the default
 *
 * defaultState :: Object | Props -> Object
 * The default state that is given if no state exists
 **/
export function withLocalStorage(storageKey, propName, setterName, clearerName, defaultState) {
  const getStorageKey = applyPropsForFunctions(storageKey);
  const getDefaultState = applyPropsForFunctions(defaultState);

  return Component => class StorageState extends React.Component {
    state = getFromStorage(getStorageKey(this.props), getDefaultState(this.props));
    storeState = (updater, callback) => {
      this.setState(updater, (...args) => {
        storage.setItem(getStorageKey(this.props), JSON.stringify(this.state));
        R.is(Function, callback) && callback(...args);
      });
    };
    clearState = () => {
      storage.removeItem(getStorageKey(this.props));
      this.setState(getDefaultState(this.props));
    };
    render() {
      const storageProps = {
        [propName]: this.state,
        [setterName]: this.storeState,
        [clearerName]: this.clearState
      };
      return <Component {...storageProps} {...this.props} />;
    }
  };
}

function applyPropsForFunctions(val) {
  return props => R.when(R.is(Function), R.applyTo(props), val);
}

/** Unused until enzyme is updated to support hooks */
export function useLocalStorage(storageKey, defaultState) {
  const [state, setState] = useState(() => getFromStorage(storageKey, defaultState));

  useEffect(
    () => setState(getFromStorage(storageKey, defaultState)),
    [storageKey, defaultState]
  );
  useEffect(
    () => {
      if (state === defaultState) {
        storage.removeItem(storageKey);
      } else {
        storage.setItem(storageKey, JSON.stringify(state));
      }
    },
    [state, storageKey, defaultState]
  );

  return [state, setState, () => setState(defaultState)];
}

const tryParse = R.tryCatch(JSON.parse, R.always(null));

function getFromStorage(storageKey, defaultState) {
  const fromStorage = tryParse(storage.getItem(storageKey));
  return R.defaultTo(defaultState, fromStorage);
}



// WEBPACK FOOTER //
// ./modules/issFrontend/utils/withLocalStorage.jsx